import { observable, action, IObservableArray } from "mobx";
import SeekerStore from "./seekerStore";
import { DCIAApplication, EmploymentStatusType } from "../models/client/dcia/DCIAApplication";
import { Demographics, Profile } from "../models/profile";
import Fetcher, { IHttpResult } from "../utils/fetcher";
import Application from "../models/application";

export default class DCIAStore {
  private _store: SeekerStore;
  @observable applicant: DCIAApplication;
  @observable firstname: string;
  @observable lastname: string;
  @observable zip: string;
  @observable day: number;
  @observable month: number;
  @observable year: number;
  @observable submitted: boolean;
  @observable validated: boolean;

  //added as a hack to use checkboxes like radio buttons
  @observable resident: boolean;
  @observable highSchoolGED: boolean;
  @observable casasTest: boolean;
  @observable felony: boolean;
  @observable misdemeanor: boolean;
  @observable drugScreenConsent: boolean;
  @observable validDriversLicense: boolean;
  @observable safetyViolations: boolean;


  constructor(store: SeekerStore) {
    this._store = store;
    this.applicant = new DCIAApplication();
    this.firstname = this.lastname = this.zip = "";
    this.day = this.month = this.year = 0;
    this.submitted = this.validated = false;    
    this.resident = this.highSchoolGED = this.casasTest = this.felony = this.misdemeanor = this.drugScreenConsent = this.validDriversLicense = this.safetyViolations = false;
  }

  @action initApplication = (): void => {
    if (!this._store.profileStore.profile) {
      this._store.profileStore.loadProfile();
    }
    if (this._store.profileStore.profile) {
      this._store.profileStore.initDemographicEdits();
    }
    const demo: Demographics = this._store.profileStore.profile.demographics;
    const profile: Profile = this._store.profileStore.profile;
    if (demo) {
      this.firstname = demo.firstName || "";
      this.lastname = demo.lastName || "";     
      this.applicant.address.street1 = demo.address.street1 || "";
      this.applicant.address.street2 = demo.address.street2 || "";
      this.applicant.address.city = demo.address.city || "";
      this.applicant.address.state = demo.address.state || "";
      this.applicant.address.zip = demo.address.zip || "";
      this.applicant.email = demo.emailAddress || "";
      this.applicant.phone = demo.phone || "";
      this.applicant.gender = demo.gender || "";
      this.day = demo.dob.getDate() || 0;
      this.month = demo.dob.getMonth() + 1 || -1;
      this.year = demo.dob.getFullYear() || 0;
      this.applicant.age = this.calculateAge(demo.dob) || 0;
    }
    if (profile) {
      this.applicant.seekerId = profile.idHash;
    }
    if (this.day !== 0 && this.month !== -1 && this.year !== 0) {
      //if birthday is autofilled this will set applicant.dob
      this.checkDate();
    }
  }

  @action calculateAge = (dob: Date): number => {
  const diff_ms = Date.now() - dob.getTime();
    const age_dt = new Date(diff_ms);
    return Math.abs(age_dt.getUTCFullYear() - 1970);
  }

  @action handleNameChange = (e: any): void => {
    this[e.target.id] = e.target.value;
  }

  @action handleDateChange = (e: any): void => {
    this[e.target.name] = e.target.value;
    if (this.day !== 0 && this.month !== -1 && this.year !== 0) {
      this.checkDate();
    }
  }

  @action handleAddressChange = (e: any): void => {
    this.applicant.address[e.target.id] = e.target.value; 
  }

  @action handleChange = (e: any): void => {
    this.applicant[e.target.id] = e.target.value;
  }

  @action handleSelectChange = (e: any): void => {
    this.applicant[e.target.name] = e.target.value;
    if (e.target.name === "employmentStatus") {
      if (this.applicant.employmentStatus === EmploymentStatusType.Disabled || this.applicant.employmentStatus === EmploymentStatusType.Retired || this.applicant.employmentStatus === EmploymentStatusType.NotEmployed) {
        this.applicant.hoursWorkedMF = "";
      }
    }
  }

  @action setCheckedValue = (prop: string, value: boolean): void => {
    this.applicant[prop] = value;
    this[prop] = true;   //allows the checkbox to be shown after initial user interaction
    if (!value) {
      if (prop === "felony") {
        this.applicant.felonyDesc = "";
      }
      if (prop === "misdemeanor") {
        this.applicant.misdemeanorDesc = "";
      }
    }
  }

  @action addRemoveFromList = (e: any, chk: boolean): void => {
    const value = e.target.value;
    this.applicant.interests = chk ? +this.applicant.interests + +value : +this.applicant.interests - +value;
  }

  @action submitForm = (): void => {
    this.applicant.name = `${this.firstname} ${this.lastname}`;
    if (this.valid()) {
      Fetcher.postWithResult("api/client/dcia/", this.applicant, action((resp: IHttpResult): void => {
        if (resp.ok) {
          this.submitted = true;
          this.applicant = new DCIAApplication();
          Fetcher.getFileDownload(`/api/client/dcia/application/${resp.id}`);
          setTimeout(action((): void => {
            this.submitted = false;
            this.validated = false;
            this.firstname = this.lastname = this.zip = "";
            this.day = this.month = this.year = 0;
            this.submitted = this.validated = false;
            this.resident = this.highSchoolGED = this.casasTest = this.felony = this.misdemeanor = this.drugScreenConsent = this.validDriversLicense = this.safetyViolations = false;
          }), 3000);
        }
        else {
          this.submitted = false;
          alert("Something went wrong, please try again");
        }
      }));
    } else {
      alert("Please complete all required fields in red before submitting.");
    }

  }

  valid = (): boolean => {
    this.validated = true;
    if (this.firstname !== "" &&
      this.lastname !== "" &&
      this.applicant.ward !== "" &&
      this.applicant.email !== "" &&
      this.applicant.phone !== "" &&
      this.applicant.gender !== "" &&
      this.resident &&
      this.highSchoolGED &&
      this.casasTest &&
      this.felony &&
      this.misdemeanor &&
      this.drugScreenConsent &&
      this.validDriversLicense &&
      this.safetyViolations &&
      this.applicant.dob !== null &&
      this.applicant.address.street1 !== "" &&
      this.applicant.address.city !== "" &&
      this.applicant.address.state !== "" &&
      this.applicant.address.zip !== "" &&
      this.applicant.employmentStatus !== EmploymentStatusType.Unknown &&
      this.applicant.interests !== 0 &&
      this.applicant.age !== 0 &&
      (this.applicant.felony && this.applicant.felonyDesc !== "" || !this.applicant.felony && this.applicant.felonyDesc.length === 0) &&
      (this.applicant.misdemeanor && this.applicant.misdemeanorDesc !== "" || !this.applicant.misdemeanor && this.applicant.misdemeanorDesc.length === 0) &&
      (((this.applicant.employmentStatus === EmploymentStatusType.FullTime || this.applicant.employmentStatus === EmploymentStatusType.PartTime) && this.applicant.hoursWorkedMF !== "") ||
      ((this.applicant.employmentStatus === EmploymentStatusType.Disabled || this.applicant.employmentStatus === EmploymentStatusType.NotEmployed || this.applicant.employmentStatus === EmploymentStatusType.Retired) && this.applicant.hoursWorkedMF.length === 0))){
      return true;
    } else {
      return false;
    }
  }

  @action checkDate = (): void => {
    const dateString = `${this.day}-${this.month}-${this.year}`;
    if (/^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/.test(dateString)) {
      const m = this.month - 1;
      this.applicant.dob = new Date(this.year, m, this.day);
    }
  }
}


// WEBPACK FOOTER //
// ./src/stores/dciaStore.ts